
import React, { useState, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import { 
  saveToSupabase, 
  saveToLocalStorage, 
  ENABLE_SUPABASE_SAVING 
} from './utils/storageUtils';
import { 
  generateQRImageURL,
  downloadQRCode as downloadQRCodeUtil
} from './utils/emailService';
import { sendConfirmationEmailWithRetry } from './utils/emailRetryService';
import ConfirmPaymentButton from './components/ConfirmPaymentButton';
import PaymentConfirmed from './components/PaymentConfirmed';

interface PaymentConfirmationButtonProps {
  hasCheckedPayment: boolean;
  setHasCheckedPayment: (value: boolean) => void;
  fullName: string;
  totalAmount: number;
  attendeeData?: any; // Data to save to Supabase
  paymentConfirmed?: boolean;
}

const PaymentConfirmationButton: React.FC<PaymentConfirmationButtonProps> = ({
  hasCheckedPayment,
  setHasCheckedPayment,
  fullName,
  totalAmount,
  attendeeData,
  paymentConfirmed = false
}) => {
  const [pulsing, setPulsing] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSendingError, setEmailSendingError] = useState<string | null>(null);
  const [qrImageURL, setQrImageURL] = useState<string | null>(null);
  
  // Add pulsing effect every few seconds to draw attention
  useEffect(() => {
    if (hasCheckedPayment) return;
    
    const interval = setInterval(() => {
      setPulsing(true);
      setTimeout(() => setPulsing(false), 1000);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [hasCheckedPayment]);
  
  // Generate QR image URL if needed for download option
  useEffect(() => {
    if (hasCheckedPayment && attendeeData) {
      const imageUrl = generateQRImageURL(attendeeData);
      setQrImageURL(imageUrl);
    }
  }, [hasCheckedPayment, attendeeData]);

  const confirmPayment = async () => {
    // Check if payment has been confirmed with checkbox
    if (!paymentConfirmed) {
      toast({
        title: "Vui lòng xác nhận thanh toán",
        description: "Hãy đánh dấu vào ô \"Tôi đã thanh toán\" để xác nhận bạn đã chuyển khoản.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Save data to Supabase if we have attendee data
      if (attendeeData && ENABLE_SUPABASE_SAVING) {
        const result = await saveToSupabase(attendeeData);
        
        if (!result?.success) {
          // Continue with local storage fallback if Supabase fails
          saveToLocalStorage(attendeeData);
        }
      } else {
        // Always save to local storage if Supabase saving is disabled
        if (attendeeData && !ENABLE_SUPABASE_SAVING) {
          saveToLocalStorage(attendeeData);
        }
      }
      
      setHasCheckedPayment(true);
      toast({
        title: "Xác nhận thanh toán thành công",
        description: `Cảm ơn **${fullName.toUpperCase()}** đã đăng ký! Tổng số tiền đóng góp: ${totalAmount.toLocaleString('vi-VN')} VNĐ`,
      });

      // After confirming payment, try to send confirmation email with retries
      if (attendeeData && attendeeData.email) {
        setIsSendingEmail(true);
        setEmailSendingError(null);
        
        // Use the enhanced email sending service with retries
        await sendConfirmationEmailWithRetry(
          attendeeData,
          // Success callback
          () => {
            setIsSendingEmail(false);
            setEmailSendingError(null);
          },
          // Error callback
          (error) => {
            setIsSendingEmail(false);
            setEmailSendingError(error);
          }
        );
      }
    } catch (error) {
      // Fallback to local storage
      saveToLocalStorage(attendeeData);
      setHasCheckedPayment(true);
      toast({
        title: "Xác nhận thanh toán thành công",
        description: `Cảm ơn **${fullName.toUpperCase()}** đã đăng ký! Tổng số tiền đóng góp: ${totalAmount.toLocaleString('vi-VN')} VNĐ`,
      });

      // Still try to send email with retries even if there was an error with database saving
      if (attendeeData && attendeeData.email) {
        setIsSendingEmail(true);
        setEmailSendingError(null);
        
        await sendConfirmationEmailWithRetry(
          attendeeData,
          // Success callback
          () => {
            setIsSendingEmail(false);
            setEmailSendingError(null);
          },
          // Error callback
          (error) => {
            setIsSendingEmail(false);
            setEmailSendingError(error);
          }
        );
      }
    }
  };

  const retryEmailSending = async () => {
    if (!attendeeData) return;
    setEmailSendingError(null);
    setIsSendingEmail(true);
    
    // Use the enhanced retry service for manual retries too
    await sendConfirmationEmailWithRetry(
      attendeeData,
      // Success callback
      () => {
        setIsSendingEmail(false);
        setEmailSendingError(null);
      },
      // Error callback
      (error) => {
        setIsSendingEmail(false);
        setEmailSendingError(error);
      }
    );
  };
  
  const downloadQRCode = () => {
    if (qrImageURL && attendeeData) {
      downloadQRCodeUtil(qrImageURL, attendeeData);
    }
  };

  return (
    <div className="w-full mt-3">
      {!hasCheckedPayment ? (
        <ConfirmPaymentButton 
          confirmPayment={confirmPayment} 
          pulsing={pulsing} 
          disabled={!paymentConfirmed}
        />
      ) : (
        <PaymentConfirmed
          isSendingEmail={isSendingEmail}
          emailSendingError={emailSendingError}
          retryEmailSending={retryEmailSending}
          downloadQRCode={downloadQRCode}
          attendeeData={attendeeData}
        />
      )}
    </div>
  );
};

export default PaymentConfirmationButton;
